import objectPath from 'object-path';

import { routerAsync } from '@/app-module';
import { i18n } from '@shared/i18n';

import Errors from '@shared/error/errors';
import Message from '@shared/message/message';

import { StripePersonService } from '@modules/stripe/stripe-person/stripe-person-service';
import { StripeConnectedAccountService } from '@modules/stripe/stripe-connected-account/stripe-connected-account-service';

const defaultRecord = {
  birthdate: {},
  address: {},
};

export default {
  namespaced: true,

  state: {
    findLoading: false,
    saveLoading: false,
    newLoading: false,
    record: defaultRecord,
    connectedAccountHasOpener: null,
    connectedAccountCurrentOwnershipPercentage: null,
    globalErrorMessage: null,
    fieldErrors: {},
  },

  getters: {
    record: (state) => state.record,
    globalErrorMessage: (state) => state.globalErrorMessage,
    fieldErrors: (state) => state.fieldErrors,
    findLoading: (state) => !!state.findLoading,
    saveLoading: (state) => !!state.saveLoading,
    newLoading: (state) => !!state.newLoading,
    connectedAccountHasOpener: (state) => state.connectedAccountHasOpener,
    connectedAccountCurrentOwnershipPercentage: (state) => state.connectedAccountCurrentOwnershipPercentage,
  },

  mutations: {
    RESET(state) {
      state.findLoading = false;
      state.saveLoading = false;
      state.newLoading = false;
      state.record = defaultRecord;
      state.globalErrorMessage = null;
      state.fieldErrors = {};
    },

    NEW_STARTED(state) {
      state.newLoading = true;
    },

    NEW_SUCCESS(state, { hasAccountOpener, currentOwnershipPercentage, connectedAccountCountry }) {
      state.connectedAccountCurrentOwnershipPercentage = currentOwnershipPercentage;
      state.connectedAccountHasOpener = hasAccountOpener;
      state.record.address.country = connectedAccountCountry;

      if (!hasAccountOpener) {
        state.record.isAccountOpener = true;
      }
      
      state.newLoading = false;
    },

    NEW_ERROR(state) {
      state.newLoading = false;
    },

    FIND_STARTED(state) {
      state.record = defaultRecord;
      state.findLoading = true;
    },

    FIND_SUCCESS(state, payload) {
      state.record = payload;
      state.findLoading = false;
    },

    FIND_ERROR(state) {
      state.record = defaultRecord;
      state.findLoading = false;
    },

    CREATE_STARTED(state) {
      state.saveLoading = true;
      state.globalErrorMessage = null;
      state.fieldErrors = {};
    },

    CREATE_SUCCESS(state) {
      state.saveLoading = false;
      state.globalErrorMessage = null;
      state.fieldErrors = {};
    },

    UPDATE_STARTED(state) {
      state.saveLoading = true;
      state.globalErrorMessage = null;
      state.fieldErrors = {};
    },

    UPDATE_SUCCESS(state) {
      state.saveLoading = false;
      state.globalErrorMessage = null;
    },

    SET_ERROR(state, { errorMessage, errors }) {
      state.saveLoading = false;

      state.globalErrorMessage = null;
      state.fieldErrors = [];

      state.globalErrorMessage = errorMessage;
      state.fieldErrors = errors;
    },

    CLEAR_FIELD_ERRORS(state, { fieldNames }) {
      for (const fieldName of fieldNames) {
        objectPath.del(state.fieldErrors, fieldName);
      }
      state.fieldErrors = Object.assign({}, state.fieldErrors);
      if (!Object.keys(state.fieldErrors).length) state.globalErrorMessage = null;
    }
  },

  actions: {
    async doNew({ commit, rootGetters }, { connectedAccountId, propertyId }) {
      try {
        commit('RESET');
        commit('NEW_STARTED');

        const currentManage = rootGetters['auth/manage/currentManage']
        const manageConnectedAccountId = 'subproperty' in currentManage && currentManage.subproperty.connectedAccountId

        if (!manageConnectedAccountId) {
          return routerAsync().push({ name: 'home' });
        } else if (connectedAccountId !== manageConnectedAccountId) {
          routerAsync().push({
            params: {
              connectedAccountId: manageConnectedAccountId,
            },
          });
        }

        const { address: { country: connectedAccountCountry } } = await StripeConnectedAccountService.find(connectedAccountId, propertyId);
        const { hasAccountOpener, currentOwnershipPercentage } = await StripeConnectedAccountService.getPersonsOverview(connectedAccountId, propertyId);

        commit('NEW_SUCCESS', {
          hasAccountOpener,
          currentOwnershipPercentage,
          connectedAccountCountry,
        });
      } catch (error) {
        Errors.handle(error);
        commit('NEW_ERROR');
        routerAsync().push('/');
      }
    },

    async doFind({ commit, rootGetters }, { id, propertyId, connectedAccountId }) {
      const currentManage = rootGetters['auth/manage/currentManage']

      try {
        commit('FIND_STARTED');

        const manageConnectedAccountId = 'subproperty' in currentManage && currentManage.subproperty.connectedAccountId
        const paymentsEnabled = 'subproperty' in currentManage && currentManage.subproperty.paymentsEnabled

        if (!paymentsEnabled) {
          return routerAsync().push({ name: 'home' });
        } else if (!manageConnectedAccountId) {
          return routerAsync().push({
            name: 'finantialSettings',
            params: {
              propertyId: currentManage.property.id,
              subpropertyId: currentManage.subproperty.id,
            },
          });
        } else if (connectedAccountId !== manageConnectedAccountId) {
          routerAsync().push({
            params: {
              connectedAccountId: manageConnectedAccountId,
            },
          });
        }

        const record = await StripePersonService.find(id, propertyId, manageConnectedAccountId);

        commit('FIND_SUCCESS', record);
      } catch (error) {
        routerAsync().push({
          name: 'finantialSettings',
          params: {
            propertyId: currentManage.property.id,
            subpropertyId: currentManage.subproperty.id,
          },
        });
        commit('FIND_ERROR');
      }
    },

    async doCreate({ commit, dispatch, rootGetters }, { personToken, phone, propertyId, subpropertyId, backToSettings = false }) {
      try {
        commit('CREATE_STARTED');
        await StripePersonService.create({ propertyId, subpropertyId, personToken, phone });
        commit('CREATE_SUCCESS');

        const currentManage = rootGetters['auth/manage/currentManage'];
        dispatch('subproperty/requirements/doFetch', {
          propertyId: currentManage.property.id,
          subpropertyId: currentManage.subproperty.id,
          userId: rootGetters['auth/currentUser'].id,
        }, {
          root: true,
        });

        Message.success(i18n('entities.stripePerson.create.success'));

        const goTo = backToSettings
          ? { name: 'finantialSettings', params: { propertyId, subpropertyId } }
          : { name: 'home' };
        routerAsync().push(goTo);
      } catch (error) {
        Errors.handle(error, (errorMessage, errors) => {
          commit('SET_ERROR', { errorMessage, errors });
        });
      }
    },

    async doUpdate({ commit, dispatch, rootGetters }, { id, stripePersonId, propertyId, subpropertyId, personToken, phone, backToSettings = false }) {
      try {
        commit('UPDATE_STARTED');
        await StripePersonService.update({ id, stripePersonId, propertyId, subpropertyId, personToken, phone });
        commit('UPDATE_SUCCESS');

        const currentManage = rootGetters['auth/manage/currentManage'];
        dispatch('subproperty/requirements/doFetch', {
          propertyId: currentManage.property.id,
          subpropertyId: currentManage.subproperty.id,
          userId: rootGetters['auth/currentUser'].id,
        }, {
          root: true,
        });

        Message.success(i18n('entities.stripePerson.update.success'));

        const goTo = backToSettings
          ? { name: 'finantialSettings', params: { propertyId, subpropertyId } }
          : { name: 'home' };
        routerAsync().push(goTo);
      } catch (error) {
        Errors.handle(error, (errorMessage, errors) => {
          commit('SET_ERROR', { errorMessage, errors });
        });
      }
    },

    doHandleStripeError({ commit }, error) {
      const wrappedError = {};
      objectPath.set(wrappedError, 'networkError.result.errors.0.code', 400);
      objectPath.set(wrappedError, 'networkError.result.errors.0.raw', error);
      objectPath.set(wrappedError, 'networkError.result.errors.0.fromStripe', true);

      Errors.handle(wrappedError, (errorMessage, errors) => {
        commit('SET_ERROR', { errorMessage, errors });
      });
    },

    doSetStripeError({ commit }, { message }) {
      commit('SET_ERROR', { errorMessage: message });
    },

    doClearStripeError({ commit }, { fieldNames }) {
      commit('CLEAR_FIELD_ERRORS', { fieldNames });
    },
  },
};
